import axios from "../api";
import _ from "lodash";
import PouchDB from "pouchdb";
import PouchDBFind from "pouchdb-find";
PouchDB.plugin(PouchDBFind);
import { dexie } from "../plugin/dexie";
import syncUtils from "../syncUtils";
import { applyUserRoleModules } from "@/utils/routerPermission";

var timer;
var repeatCount = 0;
// 持有 live pull 的引用：重连前必须 cancel 旧句柄，否则 error 重连会累积 live replication
// 连接（HTTP 长连接 + IndexedDB 事务），几十秒内耗尽句柄 → 浏览器 tab OOM 崩溃。
// eslint-disable-next-line no-unused-vars
var liveSyncHandler = null;
// 重连防重入：initMsgCB 正在进行时，error 回调不再重复触发重建
var isReconnecting = false;
// 退避重连：error 连续触发时按指数退避，避免 504 风暴下高频 cancel/重建
var reconnectTimer = null;
var reconnectDelay = 2000;

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// 失败重试：最多额外重试 maxRetries 次，间隔从 baseDelay 线性递增。对齐 galaxy-admin。
async function withRetry(fn, maxRetries = 3, baseDelay = 300) {
  let lastError;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      if (attempt < maxRetries) {
        await sleep(baseDelay * (attempt + 1));
      }
    }
  }
  throw lastError;
}

// bucketData 加载的幂等 Promise：并发只请求一次，失败后置空以便重试。
let bucketDataPromise = null;

// 已知坏 seq 区间：服务器在此区间会直接 504，导致 pull 卡死且不走 error 回调
const BAD_SEQ_START = 13030000;
const BAD_SEQ_END = 13160000;

// 从 seq 中提取数值部分（CouchDB/SG 的 seq 可能形如 "13160000" 或 "13160000-xxxx"）
function parseSeqNumber(seq) {
  if (seq === undefined || seq === null) return null;
  let strSeq = String(seq);
  strSeq = strSeq.includes(":") ? strSeq.split(":")[1] : strSeq;
  const n = parseInt(strSeq, 10);
  return Number.isNaN(n) ? null : n;
}

// 直接读 IndexedDB（idb adapter 的 local-store）里的复制 checkpoint，
// 返回 pull 方向已同步到的最大 last_seq；读不到则返回 null。
function getPullCheckpointSeq(dbName) {
  return new Promise((resolve) => {
    let req;
    try {
      req = indexedDB.open("_pouch_" + dbName);
    } catch (e) {
      resolve(null);
      return;
    }
    req.onerror = () => resolve(null);
    req.onsuccess = () => {
      const idb = req.result;
      if (!idb.objectStoreNames.contains("local-store")) {
        idb.close();
        resolve(null);
        return;
      }
      const all = idb
        .transaction("local-store", "readonly")
        .objectStore("local-store")
        .getAll();
      all.onerror = () => {
        idb.close();
        resolve(null);
      };
      all.onsuccess = () => {
        idb.close();
        const docs = all.result || [];
        // pull 的 seq 在远端序列空间（数值很大），push 的在本地空间（很小），
        // 取所有 checkpoint 中 last_seq 的最大值即 pull 位置
        for (const d of docs) {
          if (d && "last_seq" in d) {
            const n = parseSeqNumber(d.last_seq);
            if (n !== null && n > BAD_SEQ_START && n < BAD_SEQ_END) {
              if (String(d.last_seq).includes(":")) {
                const firstPart = String(d.last_seq).split(":")[0];
                resolve(firstPart + ":" + BAD_SEQ_END);
                return;
              }
              resolve(String(BAD_SEQ_END));
            }
          }
        }
        resolve(null);
      };
    };
  });
}
const deWeight = (arr) => {
  let map = new Map();
  for (let item of arr) {
    if (!map.has(item.dbId)) {
      map.set(item.dbId, item);
    } else {
      let tmp = map.get(item.dbId);
      if (
        new Date(item.createdAt).getTime() > new Date(tmp.createdAt).getTime()
      ) {
        map.set(item.dbId, item);
      }
    }
  }
  return [...map.values()];
};

const actions = {
  setLanguage({ commit }, lang) {
    commit("SET_LANG", lang);
  },
  hasData({ commit }, data) {
    commit("SAVE_HAS_DATA", data);
  },
  // 获取全选状态
  setSelectShow({ commit }, val) {
    commit("SET_SELECT_SHOW", val);
  },
  setOverallShow({ commit }, show) {
    commit("SET_OVERALL_SHOW", show);
  },
  setHandleShow({ commit }, handle) {
    commit("SET_HANDLE_SHOW", handle);
  },
  // 获取全选状态
  setPopup({ commit }, val) {
    commit("SET_SELECT_SHOW", val);
  },

  // ========== CB消息相关 ==================
  async getFeedbackUserFromDB({ commit }) {
    let result = await dexie.users.orderBy("createdAt").reverse().toArray();
    let userList = deWeight(result);
    let keepIds = new Set(userList.map((j) => j.id));
    // 批量删除重复项，避免递归调用
    let dupIds = result.filter((i) => !keepIds.has(i.id)).map((i) => i.id);
    if (dupIds.length) {
      await dexie.users.bulkDelete(dupIds);
    }
    console.log("----------会话列表-------", userList);
    commit("UPDATE_CB_USER_LIST", userList);
    return userList;
  },
  async findFeedbackUserInDB({ commit }, dbId) {
    const res = await dexie.users.get({ dbId });
    return res;
  },
  async setFeedbackUserInDB({ commit, dispatch }, msg) {
    const res = await dexie.users.add(msg);
    dispatch("getFeedbackUserFromDB");
    return "ok";
  },
  async updateFeedbackUserInDB({ commit, dispatch }, msg) {
    const res = await dexie.users.put(msg);
    dispatch("getFeedbackUserFromDB");
    return "ok";
  },
  async deleteFeedbackUser({ commit, dispatch }, id) {
    const res = await dexie.users.delete(id);
    dispatch("getFeedbackUserFromDB");
    return "ok";
  },

  // 过滤生成会话列表
  // 性能要点：原来对每条消息发起一次 PouchDB find（N 次查询 + 全表 dexie toArray），
  // 在大批量 change / rebuild 时会触发数百 ms 的 IndexedDB onsuccess 长任务。
  // 现改为按 dbId 批量聚合：3 次查询覆盖全部信息，范围限定在本次涉及的会话。
  async filterFeedbackUserFromCB({ commit, dispatch }, { arr, type = "" }) {
    let msgList = deWeight(arr);
    if (!msgList.length) return "ok";

    let dbIds = msgList.map((i) => i.dbId).filter((x) => x != null);
    let nameDbIds = msgList
      .filter((i) => i.msgType === 1)
      .map((i) => i.dbId)
      .filter((x) => x != null);

    // 仅读本次涉及的会话（走 dbId 索引），替代全表 toArray；同 dbId 多条取 createdAt 最新
    let existingMap = new Map();
    if (dbIds.length) {
      let existing = await dexie.users.where("dbId").anyOf(dbIds).toArray();
      for (let u of existing) {
        let prev = existingMap.get(u.dbId);
        if (
          !prev ||
          new Date(u.createdAt).getTime() > new Date(prev.createdAt).getTime()
        ) {
          existingMap.set(u.dbId, u);
        }
      }
    }

    // 一次查询本次涉及会话的全部未读消息，内存按 dbId 计数（替代每条消息一次 find）
    let unreadMap = new Map();
    if (dbIds.length) {
      let unreadDocs = await dispatch("getFeedbackMsgFromCb", {
        selector: {
          msgType: 0,
          dbId: { $in: dbIds },
          $or: [{ isRead: false }, { isRead: { $exists: false } }],
        },
      });
      for (let d of unreadDocs) {
        if (d.dbId == null) continue;
        unreadMap.set(d.dbId, (unreadMap.get(d.dbId) || 0) + 1);
      }
    }

    // 一次查询 msgType===1 会话的用户名消息（替代每条一次 find）；优先保留有 name 的
    let nameMap = new Map();
    if (nameDbIds.length) {
      let nameDocs = await dispatch("getFeedbackMsgFromCb", {
        selector: { msgType: 0, dbId: { $in: nameDbIds } },
      });
      for (let d of nameDocs) {
        let prev = nameMap.get(d.dbId);
        if (!prev || (!prev.name && d.name)) nameMap.set(d.dbId, d);
      }
    }

    // 构建所有需要写入的用户数据
    let toAdd = [];
    let toUpdate = [];
    for (let i = 0; i < msgList.length; i++) {
      let item = msgList[i];
      let params = {
        conversationId: item.conversationId ? item.conversationId : null,
        dbId: item.dbId,
        name: item.name || "用户",
        lastMessage: item.isRevoke ? "消息已撤回" : item.desc,
        user: item.user,
        unReadCount: Number(unreadMap.get(item.dbId) || 0),
        createdAt: new Date(item.createdAt).getTime(),
        updatedAt: new Date(item.updatedAt).getTime(),
      };

      let nameDoc = item.msgType === 1 ? nameMap.get(item.dbId) : null;
      if (nameDoc) {
        params.name = nameDoc.name ? nameDoc.name : "用户";
        if (item.user !== "onyx_admin") {
          params.unReadCount = 0;
        }
      }

      let existing = existingMap.get(item.dbId);
      if (!existing) {
        toAdd.push(params);
      } else {
        let itemTime = new Date(item.createdAt).getTime();
        if (
          itemTime > new Date(existing.createdAt).getTime() ||
          (itemTime === new Date(existing.createdAt).getTime() && item.isRevoke)
        ) {
          params.id = existing.id;
          toUpdate.push(params);
        }
      }
    }

    // 批量写入，只触发一次列表刷新
    if (toAdd.length) {
      await dexie.users.bulkAdd(toAdd);
    }
    if (toUpdate.length) {
      await dexie.users.bulkPut(toUpdate);
    }
    if (toAdd.length || toUpdate.length) {
      await dispatch("getFeedbackUserFromDB");
    }
    return "ok";
  },

  // 全量重建会话列表：仅在 Dexie users 表为空（历史欠账）时从 PouchDB 重建。
  // 兜底场景：之前 change 事件因 bug 未填充 Dexie（如 info.change.docs 报错期间），
  // 而 PouchDB 数据已落库、checkpoint 已推进，change 不会重放历史 → 列表空白。
  // 正常刷新时 Dexie 已有数据，跳过，避免每次进页面都全量重建的开销。
  async rebuildCbUserList({ commit, dispatch }) {
    try {
      const count = await dexie.users.count();
      if (count > 0) return; // Dexie 已有会话数据，无需重建
      let allMsgs = await dispatch("getFeedbackMsgFromCb", {
        selector: {},
      });
      if (!allMsgs.length) return;
      await dispatch("filterFeedbackUserFromCB", { arr: allMsgs });
    } catch (err) {
      console.error("rebuildCbUserList failed:", err);
    }
  },

  async initMsgCB({ commit, dispatch, state }) {
    // 防重入：error 回调高频触发时，避免多个 initMsgCB 并发重建
    if (isReconnecting) return;
    isReconnecting = true;
    try {
      // 取消旧的 live replication 句柄：live sync 常驻后，重连前若不 cancel，
      // 旧连接不会被回收，error 重连会指数累积句柄（HTTP 长连接 + IndexedDB 事务）→ OOM 崩溃
      if (liveSyncHandler) {
        try {
          liveSyncHandler.cancel();
        } catch (e) {
          /* ignore */
        }
        liveSyncHandler = null;
      }
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }

      let dbName = this.state.feedbackDbName;
      const remote = `${location.origin}/neocloud`;

      // 通过检查数据库是否有文档来判断是否首次同步
      const db = new PouchDB(dbName);
      const info = await db.info();
      console.log("==== db info: ", info);
      const isFirstSync = info.doc_count === 0;

      // 创建索引以加速 db.find() 查询（首次同步后也需要重建）
      await Promise.all([
        db.createIndex({ index: { fields: ["dbId", "msgType", "createdAt"] } }),
        db.createIndex({ index: { fields: ["uniqueId"] } }),
        db.createIndex({ index: { fields: ["dbId", "msgType", "isRead"] } }),
      ]);

      const opts = {
        live: true,
        retry: false,
        batch_size: 100,
        // filter: "sync_gateway/bychannel",
        query_params: {
          channels: "onyx_admin",
        },
        filter: function (doc) {
          let start = new Date(doc.createdAt).getTime();
          let end = new Date().getTime();
          var day = parseInt((end - start) / (24 * 60 * 60 * 1000));
          return day < 90;
        },
      };

      if (isFirstSync) {
        //54146984
        opts.since = "0";
      }

      // 仅在该域名下处理坏 seq 区间（其它环境不受影响）
      if (
        location.hostname === "eur-admin.onyx-international.cn" ||
        location.hostname === "localhost"
      ) {
        // 读取本地 checkpoint 判断 pull 是否已越过坏 seq 区间。
        // - 未越过（含首次同步 / 卡在坏区间前）：强制 since 跳过坏段，避免再次 504 卡死；
        // - 已越过：不传 since，让 PouchDB 从 checkpoint 自动续传，保证 live 更新持续推进。
        const checkpointSeq = await getPullCheckpointSeq(dbName);
        console.log("checkpoint last_seq:", checkpointSeq);

        if (checkpointSeq) {
          opts.since = checkpointSeq;
          console.log(
            `未越过坏区间(${BAD_SEQ_START}~${BAD_SEQ_END})，强制 since=${opts.since} 跳过`
          );
        }
      }

      // 仅保留 pull 方向常驻（live）：收消息；push 方向由发送时手动 await，两条流独立不互相 cancel
      const dbPull = new PouchDB(dbName);
      liveSyncHandler = dbPull.replicate
        .from(remote, opts)
        .on("change", function (info) {
          // 正常收到消息 = 连接健康，复位退避延迟
          reconnectDelay = 2000;
          // 单向 replicate.from 的 change 事件：info 直接是结果对象，文档数组在 info.docs
          // （双向 sync 才是 info.change.docs，此处已改为单向 pull）
          const arr = info.docs.filter(
            (item) => item.msgType === 0 || item.msgType === 1
          );
          if (!arr.length) return;
          dispatch("filterFeedbackUserFromCB", { arr });

          clearTimeout(timer);
          timer = setTimeout(() => {
            if (state.cbCurrentUser) {
              const tmp = arr.filter(
                (item) => item.dbId === state.cbCurrentUser.dbId
              );
              if (tmp.length) dispatch("mergeCbMsgList", tmp);
            }
          }, 500);
        })
        .on("paused", function (err) {
          // console.log("==pzz paused: ", err);
        })
        .on("active", function (info) {
          // console.log("== pzzactive: ", info);
        })
        .on("denied", function (err) {
          // console.log("==pzz denied: ", err);
        })
        .on("complete", function (info) {
          // console.log("==pzz complete: ", info);
        })
        .on("error", function (err) {
          // 退避重连：error 连续触发时（坏 seq 504 / 网络抖动）按指数退避，
          // 避免 504 风暴下高频 cancel/重建句柄再次累积。重连前 cancel 已在 initMsgCB 开头处理。
          console.error(
            "live pull error, reconnect in",
            reconnectDelay,
            "ms",
            err
          );
          clearTimeout(reconnectTimer);
          reconnectTimer = setTimeout(() => {
            reconnectTimer = null;
            reconnectDelay = Math.min(reconnectDelay * 2, 30000); // 封顶 30s
            dispatch("initMsgCB");
          }, reconnectDelay);
        });
    } finally {
      // 无论 await 中途抛错还是正常建立句柄，都释放重入锁；
      // error 重连走 setTimeout → dispatch("initMsgCB")，到那时锁已释放可重新进入
      isReconnecting = false;
    }
  },

  async pullCbMsgList({ commit }) {
    let dbName = this.state.feedbackDbName;
    let db = new PouchDB(dbName);
    const remote = `${location.origin}/neocloud`;
    try {
      // 通过检查数据库是否有文档来判断是否首次同步
      const info = await db.info();
      console.log("==== db info: ", info);
      const isFirstSync = info.doc_count === 0;

      const opts = {
        retry: true,
        batch_size: 100,
        filter: "sync_gateway/bychannel",
        query_params: {
          channels: "onyx_admin",
        },
      };

      // 只在首次同步时设置 since
      if (isFirstSync) {
        opts.since = "0";
      }

      const rep = await db.replicate.from(remote, opts);
      console.log("==== pull rep: ", rep);
    } catch (err) {
      console.error(err);
    }
  },

  // 推送本地消息到远程。docIds 可选：传则只推指定文档（单条发送/批量补救），不传则全量增量推
  async pushCbMsgList({ commit, dispatch }, docIds) {
    let dbName = this.state.feedbackDbName;

    try {
      const rep = await syncUtils.push(dbName, docIds);
      repeatCount = 0;
      console.log("==== push rep: ", rep);
      return rep;
    } catch (error) {
      if (repeatCount < 5) {
        repeatCount += 1;
        return await dispatch("pushCbMsgList", docIds);
      }
      repeatCount = 0;
      throw new Error(error);
    }
  },

  // 刷新页面后补救滞留消息：从 cbOutbox 读出未送达的 id，按 4 条一批重新 push
  // _id === uniqueId（见 sendMsg），可直接当作 doc_id 推送
  async drainCbOutbox({ state, commit, dispatch }) {
    const ids = Object.keys(state.cbOutbox);
    if (!ids.length) return;
    for (let i = 0; i < ids.length; i += 4) {
      const batch = ids.slice(i, i + 4);
      try {
        await dispatch("pushCbMsgList", batch); // 4 条一批，内部含 5 次重试
        batch.forEach((id) => commit("REMOVE_CB_OUTBOX", id)); // 成功的移除
      } catch (err) {
        console.error("drain batch failed:", batch, err);
        // 该批失败：标记 fail 保留，下次刷新再 drain（不无限重试，消息已在本地不丢）
        batch.forEach((id) => commit("SET_CB_OUTBOX", { id, st: "fail" }));
      }
    }
  },

  async getFeedbackMsgFromCb({ commit }, params) {
    let dbName = this.state.feedbackDbName;
    let db = new PouchDB(dbName);
    // 仅在调用方未指定 limit 时设置上限，避免覆盖 limit:1 等场景
    if (!params.limit) {
      params.limit = 10000;
    }
    const result = await db.find(params);
    let list = result.docs.filter(
      (item) => item.msgType === 0 || item.msgType === 1
    );
    list = _.orderBy(list, ["createdAt"], ["asc"]);
    return list;
  },

  async updateFeedbackMsg({ commit, dispatch }, params) {
    let dbName = this.state.feedbackDbName;
    let db = new PouchDB(dbName);
    let res = await db.put(params);
    await dispatch("pushCbMsgList", [params._id]);
    return res;
  },

  async submitFeedbackMsgToCb({ commit, dispatch }, msg) {
    let dbName = this.state.feedbackDbName;
    let db = new PouchDB(dbName);
    let res = await db.put(msg);
    let docs = await dispatch("getFeedbackMsgFromCb", {
      selector: { _id: res.id },
    });
    console.log("pzzz----submitFeedbackMsgToCb--params", msg);
    // 标记发送中（不进 db 文档，避免随同步污染对方）
    commit("ADD_CB_OUTBOX", { id: msg.uniqueId, st: "sending" });
    // push 转后台：不阻塞 UI。写本地已成功，消息可立即上屏、输入框可解锁。
    // push 结果由 cbOutbox 状态变化驱动 UI（成功→转圈消失，失败→红叹号可重发）
    dispatch("pushCbMsgList", [msg._id])
      .then(() => {
        commit("REMOVE_CB_OUTBOX", msg.uniqueId);
      })
      .catch((err) => {
        console.error("push msg failed:", msg.uniqueId, err);
        // 失败：置 fail 并保留，等用户重发或刷新 drain 补救
        commit("SET_CB_OUTBOX", { id: msg.uniqueId, st: "fail" });
      });
    console.log("pzzz----submitFeedbackMsgToCb--Done", docs);
    return docs;
  },

  // 重发：消息已在本地（首次发送时 db.put 已成功），跳过本地写入直接重新推送
  // 不能复用 submitFeedbackMsgToCb：它会再次 db.put(msg) 触发 _rev 冲突 409
  async resendCbMsg({ commit, dispatch }, msg) {
    commit("ADD_CB_OUTBOX", { id: msg.uniqueId, st: "sending" });
    dispatch("pushCbMsgList", [msg._id])
      .then(() => {
        commit("REMOVE_CB_OUTBOX", msg.uniqueId);
      })
      .catch((err) => {
        console.error("resend push failed:", msg.uniqueId, err);
        commit("SET_CB_OUTBOX", { id: msg.uniqueId, st: "fail" });
      });
  },

  mergeCbMsgList({ commit, dispatch }, arr) {
    commit("MERGE_CB_MSG_LIST", arr);
  },
  updateCbMsgList({ commit, dispatch }, list) {
    commit("UPDATE_CB_MSG_LIST", list);
  },
  setCbCurrentUser({ commit, dispatch }, item) {
    commit("SET_CB_CURRENT_USER", item);
  },

  // ==================================================

  // =============== 第三方服务配置 =====================

  // 获取第三方服务配置详情
  async getServiceConfigDetails({ commit, dispatch }, id) {
    try {
      let dbName = "CloudConfig";
      let db = new PouchDB(dbName);
      let params = {
        selector: {
          _id: id,
          msgType: 4,
        },
      };
      let res = await db.find(params);
      return res.docs;
    } catch (error) {
      throw new Error(error);
    }
  },

  //  删除第三方服务配置
  async delServiceConfigItem({ commit, dispatch }, params) {
    try {
      let dbName = "CloudConfig";
      let db = new PouchDB(dbName);
      let res = await db.remove(params);
      await syncUtils.push(dbName);
      return res;
    } catch (error) {
      throw new Error(error);
    }
  },

  //  更新第三方服务配置
  async updateServiceConfigItem({ commit, dispatch }, params) {
    try {
      let dbName = "CloudConfig";
      let db = new PouchDB(dbName);
      let res = await db.put(params);
      await syncUtils.push(dbName);
      return res;
    } catch (error) {
      throw new Error(error);
    }
  },

  // ==================================================

  // 验证路由权限
  getRouter({ commit, state }, role_id) {
    return new Promise((resolve, reject) => {
      axios
        .getRoleModules(role_id)
        .then((res) => {
          let routerRole = applyUserRoleModules(
            res.data.rows.map((item) => item.name),
            state.userInfo
          );
          commit("SET_ROUTER", routerRole);
          resolve("ok");
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  // 获取用户信息
  getUserInfo({ commit }) {
    return new Promise((resolve, reject) => {
      axios
        .getUser()
        .then((res) => {
          commit("USER_INFO", res.data);
          resolve(res.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  // 获取书籍分类
  getBookCategories({ commit }) {
    return new Promise((resolve, reject) => {
      axios.getBookCategories().then((res) => {
        commit("SAVE_CATEGORIES", res.data);
        resolve(res.data);
      });
    });
  },

  // 获取频道
  getBookChannels({ commit }, lang) {
    return new Promise((resolve, reject) => {
      let params = {
        lang: lang || "",
      };
      axios.getBookChannels(params).then((res) => {
        commit("SAVE_CHANNELS", res.data);
        resolve(res.data);
      });
    });
  },

  // 获取bucket数据（幂等：并发只请求一次，失败自动重试 3 次，仍失败则置空以便下次再试）
  getBucketData({ commit, state }) {
    const loaded =
      state.bucketData &&
      state.bucketData.common &&
      state.bucketData.common.bucket;
    if (loaded) {
      return Promise.resolve(state.bucketData);
    }
    if (!bucketDataPromise) {
      bucketDataPromise = (async () => {
        const res = await withRetry(() => axios.getGalaxyOssModule(), 3);
        const bucketData = res.data || res;
        commit("setBucketData", bucketData);
        if (!(bucketData && bucketData.common && bucketData.common.bucket)) {
          bucketDataPromise = null;
        }
        return bucketData;
      })();
      bucketDataPromise.catch(() => {
        bucketDataPromise = null;
      });
    }
    return bucketDataPromise;
  },
};

export default actions;
